import React, { useEffect, useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import SnackbarContent from '@mui/material/SnackbarContent';
import { DataProviderInstance } from '../../api/DataProvider';
import { isOwnerOrAdmin } from '../../utils/RoleManager';
import { useDemoData } from '../demo-data/demoData';
import useDemoDataRemoval from '../demo-data/useDemoDataRemoval';
import { useGuidedTour } from './GuidedTourContext';
import styles from './DemoCleanupNudge.module.css';

/** Disparado pelo cadastro de pessoa (CreatePatientPopup) quando uma pessoa nova é salva. */
export const PERSON_CREATED_EVENT = 'gabriela:personCreated';

const storageKey = (companyId) => `gabriela.demoCleanupNudge.${companyId}`;

const alreadyShown = (companyId) => {
  try { return window.localStorage.getItem(storageKey(companyId)) === '1'; } catch (error) { return false; }
};

const markShown = (companyId) => {
  try { window.localStorage.setItem(storageKey(companyId), '1'); } catch (error) { /* sem armazenamento: pode voltar a aparecer */ }
};

/**
 * Aviso discreto, uma vez por conta, quando a primeira pessoa de verdade é cadastrada e
 * os exemplos ainda estão na conta: "quer tirar os exemplos agora?". Nada é removido
 * sozinho (docs/features/trial-demo-data, R7): "Tirar agora" abre a mesma confirmação
 * da faixa (useDemoDataRemoval); "Depois" só fecha. Espera o guia terminar para não
 * cobrir o balão nem a comemoração do passo.
 */
function DemoCleanupNudge() {
  const { session } = DataProviderInstance();
  const companyId = session?.currentCompany;
  const { removable } = useDemoData();
  const canRemove = isOwnerOrAdmin();
  const tour = useGuidedTour();
  const demoRemoval = useDemoDataRemoval();
  const [pending, setPending] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (companyId == null || !removable || !canRemove) return undefined;
    const onCreated = () => {
      if (!alreadyShown(companyId)) setPending(true);
    };
    window.addEventListener(PERSON_CREATED_EVENT, onCreated);
    return () => window.removeEventListener(PERSON_CREATED_EVENT, onCreated);
  }, [companyId, removable, canRemove]);

  useEffect(() => {
    if (!pending || tour.activeKey) return;
    setPending(false);
    if (companyId == null || !removable || alreadyShown(companyId)) return;
    markShown(companyId);
    setOpen(true);
  }, [pending, tour.activeKey, companyId, removable]);

  const later = (event, reason) => {
    // Clique fora não fecha: a pessoa escolhe; o aviso some sozinho depois de um tempo
    if (reason === 'clickaway') return;
    setOpen(false);
  };
  const removeNow = () => {
    setOpen(false);
    demoRemoval.requestRemoval();
  };

  return (
    <>
      <Snackbar
        open={open}
        onClose={later}
        autoHideDuration={20000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <SnackbarContent
          className={styles.content}
          message={(
            <span className={styles.message}>
              <strong>Primeira pessoa cadastrada.</strong>
              <span>Quer tirar os exemplos agora? Também dá para deixar e remover depois pela faixa no topo.</span>
            </span>
          )}
          action={(
            <span className={styles.actions}>
              <button type="button" className={styles.later} onClick={later}>Depois</button>
              <button type="button" className={styles.remove} onClick={removeNow}>Tirar agora</button>
            </span>
          )}
        />
      </Snackbar>
      {demoRemoval.element}
    </>
  );
}

export default DemoCleanupNudge;
